import {Component} from 'panel';

import {registerMPElement} from '../../util/register-element';
import {getClosest} from '../../util/dom';
import template from './index.jade';
import ContextPanelInternal from './internal';

class ContextPanel extends Component {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      ...ContextPanelInternal.attrsSchema,
      'position-parent-selector': `string`,
      'force-left': {
        type: `boolean`,
        default: false,
      },
    };
  }

  get config() {
    return {
      template,
      useShadowDom: true,
      helpers: {
        actionClick: (ev) => {
          this.dispatchEvent(
            new CustomEvent(`tooltipContextAction`, {
              composed: true,
              cancelable: true,
              detail: ev.detail,
            }),
          );
        },
      },
    };
  }

  setUpTooltip() {
    this.tooltip = document.createElement(`mp-context-panel-internal`);
    this.tooltip.addEventListener(`actionClick`, this.helpers.actionClick);
    this.tooltip.setAttribute(`tooltip-data`, JSON.stringify(this.attr(`tooltip-data`)));
    document.body.appendChild(this.tooltip);
  }

  tearDownTooltip() {
    if (this.tooltip) {
      if (document.body.contains(this.tooltip)) {
        document.body.removeChild(this.tooltip);
      }
      this.tooltip = null;
    }
  }

  connectedCallback() {
    super.connectedCallback();
    const positionElementSelector = this.attr(`position-parent-selector`);
    this.positionElement =
      this.tooltipPositionParentElement || getClosest(this, positionElementSelector) || this.previousSibling;

    this.onResize = () => requestAnimationFrame(() => this.setTooltipPositioning());
  }

  disconnectedCallback() {
    this.tearDownTooltip();
    super.disconnectedCallback();
    window.removeEventListener(`resize`, this.onResize);
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    if (attr === `tooltip-data` && this.attr(`tooltip-data`) && !this.tooltip) {
      // lazy create internal tooltip elem when there is data to be displayed
      this.setUpTooltip();
    }
    if (this.tooltip) {
      this.tooltip.setAttribute(attr, this.getAttribute(attr));
      // If the user passes in an element passed in by props, use it for positioning.
      // Otherwise fallback to using the element defined by the selector
      this.setTooltipPositioning();
      window.addEventListener(`resize`, this.onResize);
    }
  }

  setTooltipPositioning() {
    if (!this.positionElement || !this.attr(`tooltip-data`)) return;
    const padding = 2;
    const rect = this.positionElement.getBoundingClientRect();
    const tooltipWidth = this.tooltip.offsetWidth;
    const windowWidth = window.innerWidth;
    this.tooltip.style.top = `${window.scrollY + rect.top}px`;

    // ensure tooltip is displayed within viewport
    if (this.attr(`force-left`) || rect.right + tooltipWidth > windowWidth) {
      this.tooltip.style.left = `${window.scrollX + rect.left - tooltipWidth - padding}px`;
    } else {
      this.tooltip.style.left = `${window.scrollX + rect.right + padding}px`;
    }
  }

  set tooltipPositionParentElement(el) {
    this.positionElement = el;
    if (this.tooltip) {
      this.setTooltipPositioning();
    }
  }
  get tooltipPositionParentElement() {
    return this.positionElement;
  }
}

export default registerMPElement(`mp-context-panel`, ContextPanel);
